// mp2026 — frontend JS entry.
// Vite watches this; HMR is automatic in dev.
//
// Note: main.css is enqueued separately by functions.php so it lands in <head>
// (avoids FOUC). Don't import it here.

// Flags JS as available so main.css can key progressive-enhancement CSS off
// `html.js` (e.g. process-tabs below: no JS → every step's panel stacks
// visible; JS present → main.css collapses to just the active one and this
// file drives which is active).
document.documentElement.classList.add( 'js' );

/**
 * Variation swatches for variable products (t-shirt, hoodie). WooCommerce
 * renders each variation attribute as a plain <select> inside
 * `form.variations_form table.variations`; the old site layered image
 * swatches over those selects via a plugin we intentionally dropped. This
 * rolls our own with zero plugin: for each select we render a row of CSS
 * chips (color attrs → solid brand-mapped color dots, everything else →
 * text pills), hide the native select, and bridge clicks back to it so
 * WooCommerce's own add-to-cart-variation.js still drives price/image/
 * Add-to-cart. jQuery is guaranteed on a Woo product page (that script
 * depends on it) and is the only way to both trigger and observe Woo's
 * custom variation events — but the swatches degrade to the native select
 * with no JS at all (we only hide the select once chips exist).
 *
 * Colors are mapped from the pa_color term slug. Unknown slugs fall back to
 * a text pill, so a new color added in wp-admin never renders a blank dot.
 *
 * NOTE: this map is declared at module top — above the init calls below —
 * on purpose. `const` is not hoisted (temporal dead zone), and
 * buildSwatches() reads it synchronously during initVariationSwatches();
 * declaring it after the call throws a ReferenceError and no swatch builds.
 */
const MP_SWATCH_COLORS = {
	'black': '#1a1a1a',
	'indigo': '#3b3b6d',
	'light-blue': '#a9c9e8',
	'yellow': '#f4c400',
	'maroon': '#7b1e1e',
	'sage': '#9caf88',
	'slate-blue': '#5f6f8f',
};

initProcessTabs();
initCopyLinkButtons();
initFormTimeTrap();
initVariationSwatches();
initReviewShuffle();
initHeroVideo();
initGalleryDrift();
initCountUp();
initMotion();
initFloatCta();

/**
 * Floating "Book a Call" CTA that follows the user down the page (client
 * 2026-08). Appears after scrolling past the hero, hides back near the top and
 * when the footer is close so it never overlaps the page's own closing CTA.
 * Progressive enhancement — injected here, hidden from AT (the header keeps an
 * equivalent CTA).
 */
function initFloatCta() {
	if ( document.querySelector( '.mp-float-cta' ) ) {
		return;
	}
	const cta = document.createElement( 'a' );
	cta.href = '/start/';
	cta.className = 'mp-float-cta';
	cta.textContent = 'Book a Call';
	cta.setAttribute( 'aria-hidden', 'true' );
	cta.setAttribute( 'tabindex', '-1' );
	document.body.appendChild( cta );

	const update = () => {
		const past = window.scrollY > 640;
		const nearBottom = ( window.innerHeight + window.scrollY ) > ( document.body.offsetHeight - 340 );
		cta.classList.toggle( 'is-visible', past && ! nearBottom );
	};
	update();
	window.addEventListener( 'scroll', update, { passive: true } );
	window.addEventListener( 'resize', update, { passive: true } );
}

/**
 * Defer every [data-mp-video] reel so it doesn't compete with first paint. The
 * video renders with only its poster (fast LCP, ~40KB) and no src; the real
 * source is set later. A ~1.6MB reel downloading eagerly under `autoplay`
 * otherwise saturated bandwidth on load and pushed FCP/LCP into the red.
 * No-JS visitors keep the poster.
 *
 * This used to be `querySelector` — singular — so a page with two reels played
 * only the first and left the second as a dead poster forever. Case studies
 * carry more than one (a product demo and a screen capture, say), so it walks
 * all of them now.
 *
 * Two behaviours, because the hero and an in-page reel want opposite things:
 *
 *   hero (data-mp-video-hero) — loads on window load, and NOT AT ALL on mobile.
 *     It is the LCP element there, and 1.6MB in front of first paint is the
 *     single biggest mobile-LCP regression this theme has had.
 *   everything else — loads when it scrolls near the viewport, on every screen
 *     size. It is never the LCP element, it is already behind a poster, and a
 *     mid-page reel that stays frozen on mobile is just a broken-looking image.
 */
function initHeroVideo() {
	const videos = document.querySelectorAll( 'video[data-mp-video]' );
	if ( ! videos.length ) {
		return;
	}

	const load = ( video ) => {
		if ( video.dataset.mpVideoLoaded ) {
			return;
		}
		video.dataset.mpVideoLoaded = '1';
		video.src = video.dataset.mpVideo;
		video.load();
		const p = video.play();
		if ( p && typeof p.catch === 'function' ) {
			p.catch( () => {} ); // autoplay may be blocked; poster stays
		}
	};

	const desktop = window.matchMedia( '(min-width: 782px)' ).matches;
	const inView = 'IntersectionObserver' in window
		? new IntersectionObserver( ( entries, obs ) => {
			entries.forEach( ( e ) => {
				if ( e.isIntersecting ) {
					load( e.target );
					obs.unobserve( e.target );
				}
			} );
		}, { rootMargin: '200px 0px' } )
		: null;

	videos.forEach( ( video ) => {
		if ( video.hasAttribute( 'data-mp-video-hero' ) ) {
			if ( ! desktop ) {
				return; // poster stays — see note above
			}
			if ( document.readyState === 'complete' ) {
				load( video );
			} else {
				window.addEventListener( 'load', () => load( video ), { once: true } );
			}
			return;
		}
		if ( inView ) {
			inView.observe( video );
		} else {
			load( video );
		}
	} );
}

/**
 * "Success in numbers" stat bar: count each number up from 0 when it scrolls
 * into view (QA: "the numbers count up when we scroll to it"). Numeric target,
 * decimals, and any inline suffix (e.g. "%") are baked into data attributes by
 * the [mp2026_stats_bar] shortcode; the trailing <small> (" star", "/100")
 * stays static. Fires once per element via IntersectionObserver; reduced-motion
 * users get the final value with no animation.
 */
function initCountUp() {
	const els = document.querySelectorAll( '.stat-num[data-mp-count-to]' );
	if ( ! els.length ) {
		return;
	}
	const reduce = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
	const format = ( value, decimals, suffix ) =>
		( decimals > 0 ? value.toFixed( decimals ) : String( Math.round( value ) ) ) + suffix;

	const run = ( el ) => {
		const target = parseFloat( el.dataset.mpCountTo );
		const decimals = parseInt( el.dataset.mpDecimals, 10 ) || 0;
		const suffix = el.dataset.mpSuffix || '';
		const final = el.textContent; // exact display string to land on
		if ( ! isFinite( target ) ) {
			return;
		}
		if ( reduce ) {
			el.textContent = final;
			return;
		}
		const duration = 1400;
		let start = null;
		const step = ( ts ) => {
			if ( null === start ) {
				start = ts;
			}
			const p = Math.min( 1, ( ts - start ) / duration );
			const eased = 1 - Math.pow( 1 - p, 3 ); // easeOutCubic
			if ( p < 1 ) {
				el.textContent = format( target * eased, decimals, suffix );
				window.requestAnimationFrame( step );
			} else {
				el.textContent = final; // exact, avoids rounding drift
			}
		};
		el.textContent = format( 0, decimals, suffix );
		window.requestAnimationFrame( step );
	};

	if ( ! ( 'IntersectionObserver' in window ) ) {
		els.forEach( run );
		return;
	}
	const io = new IntersectionObserver(
		( entries, obs ) => {
			entries.forEach( ( entry ) => {
				if ( entry.isIntersecting ) {
					run( entry.target );
					obs.unobserve( entry.target );
				}
			} );
		},
		{ threshold: 0.4 }
	);
	els.forEach( ( el ) => io.observe( el ) );
}

/**
 * MP MOTION MODULE trigger — full contract in theme/inc/motion.php.
 * Opt the page into motion by adding `js-motion` to <html>, but ONLY when the
 * visitor hasn't requested reduced motion (so the CSS module never animates for
 * them) — and because this class is what unlocks the pre-animation "empty"
 * state, doing it from JS means a failed/absent script leaves every mark at its
 * FINAL value. Then add `.is-inview` to each `.mp-motion` graphic ONCE as it
 * scrolls in, which fires its draw-on. Numbers count up via initCountUp().
 */
function initMotion() {
	if ( window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		return;
	}
	// Tell the <head> pre-paint arm (functions.php) that we're alive, so its
	// 3s "reveal everything" JS-failure fallback stays a no-op.
	window.__mpMotionRan = true;
	document.documentElement.classList.add( 'js-motion' );
	const blocks = document.querySelectorAll( '.mp-motion' );
	if ( ! blocks.length ) {
		return;
	}
	// Adding `js-motion` flips every mark to its EMPTY pre-draw state. Let that
	// paint for one frame BEFORE we start adding `.is-inview` — otherwise an
	// above-the-fold mark (already intersecting at load) gets `.is-inview` in
	// the same frame the empty state is set, the browser coalesces the two, and
	// the draw-on transition is skipped ("happens instantly or not at all").
	// The double-rAF guarantees the empty frame renders, so the 1.55s draw runs.
	const startObserving = () => {
		if ( ! ( 'IntersectionObserver' in window ) ) {
			blocks.forEach( ( el ) => el.classList.add( 'is-inview' ) );
			return;
		}
		const io = new IntersectionObserver(
			( entries, obs ) => {
				entries.forEach( ( entry ) => {
					if ( entry.isIntersecting ) {
						entry.target.classList.add( 'is-inview' );
						obs.unobserve( entry.target );
					}
				} );
			},
			{ threshold: 0.35 }
		);
		blocks.forEach( ( el ) => io.observe( el ) );
	};
	window.requestAnimationFrame( () => window.requestAnimationFrame( startObserving ) );
}

/**
 * Horizontal scroll-drift for the About culture gallery — replicates live
 * mediaproper.com/about-us/'s Ghostkit scroll effect (translateX ~-80→+30px as
 * the band travels the viewport). The row is flex-centered in CSS; this only
 * adds the translateX delta. Desktop + motion-OK only (mobile is a swipe strip,
 * see main.css); rAF-throttled, passive listeners.
 */
function initGalleryDrift() {
	const bands = document.querySelectorAll( '[data-mp-drift]' );
	if ( ! bands.length ) {
		return;
	}
	const desktop = window.matchMedia( '(min-width: 782px)' );
	const reduce = window.matchMedia( '(prefers-reduced-motion: reduce)' );
	const FROM = -80;
	const TO = 30;

	bands.forEach( ( band ) => {
		const row = band.querySelector( '.about-gallery__row' );
		if ( ! row ) {
			return;
		}
		let ticking = false;
		const update = () => {
			ticking = false;
			if ( ! desktop.matches || reduce.matches ) {
				row.style.transform = '';
				return;
			}
			const rect = band.getBoundingClientRect();
			const vh = window.innerHeight || document.documentElement.clientHeight;
			// 0 when the band's center is at the viewport bottom (just entering),
			// 1 when it reaches the top (leaving) — clamped to that travel.
			const center = rect.top + rect.height / 2;
			const progress = Math.max( 0, Math.min( 1, 1 - center / vh ) );
			const x = FROM + ( TO - FROM ) * progress;
			row.style.transform = `translateX(${ x }px)`;
		};
		const onScroll = () => {
			if ( ! ticking ) {
				ticking = true;
				window.requestAnimationFrame( update );
			}
		};
		window.addEventListener( 'scroll', onScroll, { passive: true } );
		window.addEventListener( 'resize', onScroll, { passive: true } );
		update();
	} );
}

/**
 * Randomize the order of the client-review cards on each load. The reviews
 * (Google, via inc/reviews.php) are all rendered server-side; shuffling
 * client-side means the rotation survives full-page caching (live serves
 * cached HTML — a server-side shuffle would freeze one order until the
 * cache expired). Fisher–Yates over the card elements.
 */
function initReviewShuffle() {
	document.querySelectorAll( '[data-mp-shuffle]' ).forEach( ( rail ) => {
		const cards = Array.from( rail.children );
		for ( let i = cards.length - 1; i > 0; i-- ) {
			const j = Math.floor( Math.random() * ( i + 1 ) );
			[ cards[ i ], cards[ j ] ] = [ cards[ j ], cards[ i ] ];
		}
		// data-mp-limit caps how many show per load — with all reviews in the
		// DOM, each visit surfaces a different random subset (rotation across
		// the full set, cache-proof since it's client-side). 0/unset = show all.
		const limit = parseInt( rail.dataset.mpLimit, 10 ) || 0;
		cards.forEach( ( card, i ) => {
			if ( limit && i >= limit ) {
				card.remove();
			} else {
				rail.appendChild( card );
			}
		} );
		// Re-appending children under scroll-snap:x-mandatory makes the
		// browser follow the moved snap target, leaving the rail scrolled
		// mid-way (first card clipped on desktop). Snap back to the start.
		rail.scrollLeft = 0;
	} );
}

function initVariationSwatches() {
	const $ = window.jQuery;
	document.querySelectorAll( 'form.variations_form' ).forEach( ( form ) => {
		const selects = form.querySelectorAll( '.variations select' );
		if ( ! selects.length ) {
			return;
		}
		selects.forEach( ( select ) => buildSwatches( select, $ ) );

		// Default-select the first option of every attribute so a full
		// variation resolves on load. Without this, WooCommerce can't match a
		// single variation until ALL attributes are chosen, so changing just
		// the color (with size still blank) wouldn't swap the product image.
		// With sensible defaults, changing one attribute updates the image
		// immediately. Set all values first, then fire one change so Woo
		// resolves against the complete selection.
		let mp2026_defaulted = false;
		selects.forEach( ( select ) => {
			if ( select.value ) {
				return;
			}
			const first = Array.from( select.options ).find( ( o ) => o.value && ! o.disabled );
			if ( first ) {
				select.value = first.value;
				mp2026_defaulted = true;
			}
		} );
		if ( mp2026_defaulted ) {
			const last = selects[ selects.length - 1 ];
			if ( $ ) {
				$( last ).trigger( 'change' );
			} else {
				last.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			}
			selects.forEach( reflectSwatches );
		}

		// Woo enables/disables options as the shopper narrows a selection,
		// and clears them via the "Clear" link — reflect that into the chips.
		if ( $ ) {
			$( form ).on(
				'woocommerce_update_variation_values reset_data check_variations woocommerce_variation_select_change',
				() => form.querySelectorAll( '.variations select' ).forEach( reflectSwatches )
			);

			// Swap the gallery image on variation change. WooCommerce's
			// classic add-to-cart-variation.js only updates the image inside
			// its own classic gallery markup; the block single-product
			// template's `woocommerce/product-image-gallery` block isn't
			// wired to it, so choosing a color changed the variation (price/
			// id/stock) but the photo stayed put. Do it ourselves off the
			// `found_variation` event, and restore the original on reset.
			$( form ).on( 'found_variation', ( _e, variation ) => mp2026_swap_gallery_image( variation ) );
			$( form ).on( 'reset_data', () => mp2026_swap_gallery_image( null ) );
		}
	} );
}

/**
 * Swap the main product-gallery image to the selected variation's image.
 * `variation` is WooCommerce's found_variation payload (variation.image has
 * src/srcset/sizes/full_src/…); pass null to restore the original product
 * image (on "Clear"/reset). Guards the original src on first run so reset
 * always has something to go back to.
 */
function mp2026_swap_gallery_image( variation ) {
	const img = document.querySelector(
		'.woocommerce-product-gallery__image img, .wp-post-image'
	);
	if ( ! img ) {
		return;
	}
	if ( ! img.dataset.mpOrigSrc ) {
		img.dataset.mpOrigSrc = img.getAttribute( 'src' ) || '';
		img.dataset.mpOrigSrcset = img.getAttribute( 'srcset' ) || '';
	}
	const link = img.closest( 'a' );
	if ( variation && variation.image && variation.image.src ) {
		img.src = variation.image.src;
		img.srcset = variation.image.srcset || '';
		img.sizes = variation.image.sizes || '';
		if ( link && variation.image.full_src ) {
			link.href = variation.image.full_src;
		}
	} else {
		img.src = img.dataset.mpOrigSrc;
		img.srcset = img.dataset.mpOrigSrcset;
	}
}

function buildSwatches( select, $ ) {
	if ( select.dataset.mpSwatched ) {
		return;
	}
	const isColor = /(^|_)color$/.test( select.name || select.id );

	const list = document.createElement( 'div' );
	list.className = 'mp-swatches' + ( isColor ? ' mp-swatches--color' : ' mp-swatches--pill' );
	list.setAttribute( 'role', 'radiogroup' );
	const labelCell = select.closest( 'tr' )?.querySelector( 'th label' );
	if ( labelCell?.id ) {
		list.setAttribute( 'aria-labelledby', labelCell.id );
	}

	Array.from( select.options ).forEach( ( option ) => {
		if ( ! option.value ) {
			return; // skip "Choose an option"
		}
		const hex = isColor ? MP_SWATCH_COLORS[ option.value ] : null;
		const chip = document.createElement( 'button' );
		chip.type = 'button';
		chip.className = 'mp-swatch' + ( hex ? ' mp-swatch--color' : ' mp-swatch--pill' );
		chip.dataset.value = option.value;
		chip.setAttribute( 'role', 'radio' );
		chip.setAttribute( 'aria-checked', 'false' );
		chip.title = option.textContent;
		chip.setAttribute( 'aria-label', option.textContent );
		if ( hex ) {
			chip.style.setProperty( '--mp-swatch-color', hex );
		} else {
			chip.textContent = option.textContent;
		}
		chip.addEventListener( 'click', () => {
			// Radiogroup semantics: a click always SELECTS this option; it never
			// toggles off. The old "click again to clear" logic broke add-to-cart:
			// initVariationSwatches() default-selects the first option of every
			// attribute (so size=large, color=maroon on load), so a shopper who
			// clicks the already-defaulted chip (e.g. "Large") would CLEAR it,
			// leaving the size unset → Woo matches no variation → variation_id
			// stays empty → "Please select some product options" alert on add.
			// Clearing is still available via Woo's own "Clear" link.
			select.value = option.value;
			if ( $ ) {
				$( select ).trigger( 'change' );
			} else {
				select.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			}
			reflectSwatches( select );
		} );
		list.appendChild( chip );
	} );

	select.insertAdjacentElement( 'afterend', list );
	select.dataset.mpSwatched = '1';
	// Only now hide the native control — no-JS visitors keep the real select.
	select.classList.add( 'mp-swatched' );
	reflectSwatches( select );
}

function reflectSwatches( select ) {
	const list = select.nextElementSibling;
	if ( ! list || ! list.classList.contains( 'mp-swatches' ) ) {
		return;
	}
	const disabled = new Set(
		Array.from( select.options ).filter( ( o ) => o.disabled ).map( ( o ) => o.value )
	);
	list.querySelectorAll( '.mp-swatch' ).forEach( ( chip ) => {
		const selected = chip.dataset.value === select.value;
		chip.classList.toggle( 'is-selected', selected );
		chip.setAttribute( 'aria-checked', selected ? 'true' : 'false' );
		const off = disabled.has( chip.dataset.value );
		chip.classList.toggle( 'is-disabled', off );
		chip.disabled = off;
	} );
}

/**
 * Contact/start-project form time-trap (theme/inc/forms.php) — stamps each
 * `[data-mp-form]` form's hidden `ts` field with the page-load time
 * (Date.now(), ms) so the server can reject submissions filled in under 3s
 * (bots) or replayed from a page loaded more than 24h ago. No-JS visitors
 * submit with `ts` empty/"0" — the server treats that the same as a bot,
 * which is an acceptable tradeoff since the honeypot alone still covers
 * the no-JS spam case.
 */
function initFormTimeTrap() {
	const now = String( Date.now() );
	document.querySelectorAll( '[data-mp-form] input[name="ts"]' ).forEach( ( input ) => {
		input.value = now;
	} );
}

/**
 * Process-in-detail step tabs — STEP 1 Discovery … STEP 5 Deliver on
 * /process/ (both the Web Design and Digital Marketing groups) and the
 * same widget on the 3 service pages, all rendered by the shared
 * `mp2026_process_tabs()` helper (theme/inc/pattern-helpers.php). Click a
 * step, or move focus to a tab and press ArrowLeft/ArrowRight/Home/End, to
 * switch which panel is showing (roving tabindex — only the active tab is
 * in the Tab-key sequence, matching the WAI-ARIA tabs pattern).
 */
function initProcessTabs() {
	const tablists = document.querySelectorAll( '.process-tabs[role="tablist"]' );

	tablists.forEach( ( tablist ) => {
		const tabs = Array.from( tablist.querySelectorAll( '[role="tab"]' ) );
		if ( ! tabs.length ) {
			return;
		}

		tabs.forEach( ( tab, index ) => {
			tab.addEventListener( 'click', () => activateProcessTab( tabs, index ) );
			tab.addEventListener( 'keydown', ( event ) => {
				let target;
				switch ( event.key ) {
					case 'ArrowRight':
					case 'ArrowDown':
						target = ( index + 1 ) % tabs.length;
						break;
					case 'ArrowLeft':
					case 'ArrowUp':
						target = ( index - 1 + tabs.length ) % tabs.length;
						break;
					case 'Home':
						target = 0;
						break;
					case 'End':
						target = tabs.length - 1;
						break;
					default:
						return;
				}
				event.preventDefault();
				activateProcessTab( tabs, target );
				tabs[ target ].focus();
			} );
		} );
	} );
}

function activateProcessTab( tabs, activeIndex ) {
	tabs.forEach( ( tab, index ) => {
		const active = index === activeIndex;
		tab.classList.toggle( 'is-active', active );
		tab.setAttribute( 'aria-selected', active ? 'true' : 'false' );
		tab.setAttribute( 'tabindex', active ? '0' : '-1' );

		const panel = document.getElementById( tab.getAttribute( 'aria-controls' ) );
		if ( panel ) {
			panel.classList.toggle( 'is-active', active );
		}
	} );
}

/**
 * Blog post "Copy Link" share button (theme/inc/insights.php's
 * mp2026_blog_meta shortcode) — real `navigator.clipboard` write, not a
 * decorative icon. Briefly swaps the aria-label + adds `.is-copied` (main.css)
 * for visible + screen-reader feedback, then reverts after 2s. Falls back to
 * a legacy `document.execCommand( 'copy' )` path for browsers without the
 * async Clipboard API (still supported, just deprecated).
 */
function initCopyLinkButtons() {
	document.querySelectorAll( '.blog-share__btn--copy' ).forEach( ( button ) => {
		button.addEventListener( 'click', () => copyLink( button ) );
	} );
}

function copyLink( button ) {
	const url = button.getAttribute( 'data-copy-url' );
	if ( ! url ) {
		return;
	}

	const onCopied = () => {
		const original = button.getAttribute( 'aria-label' );
		button.classList.add( 'is-copied' );
		button.setAttribute( 'aria-label', 'Link copied!' );
		window.setTimeout( () => {
			button.classList.remove( 'is-copied' );
			button.setAttribute( 'aria-label', original );
		}, 2000 );
	};

	if ( navigator.clipboard && window.isSecureContext ) {
		navigator.clipboard.writeText( url ).then( onCopied ).catch( () => {} );
		return;
	}

	const input = document.createElement( 'textarea' );
	input.value = url;
	input.setAttribute( 'readonly', '' );
	input.style.position = 'absolute';
	input.style.left = '-9999px';
	document.body.appendChild( input );
	input.select();
	try {
		document.execCommand( 'copy' );
		onCopied();
	} catch ( e ) {
		// Clipboard unavailable — no-op, button remains a plain (non-broken) control.
	}
	document.body.removeChild( input );
}


/* LOGO COLORWAY EASTER EGG
 * Hovering the MP badge (header or footer) cycles its ring/disc through the
 * brand-book colorway pairs — one every 250ms — and settles back to brand on
 * leave. Colours ride two CSS custom properties on the anchor (see the
 * .mp-ring/.mp-disc rules in main.css); nothing is swapped in the DOM, so it
 * costs nothing until the first hover.
 *
 * Deliberate limits:
 * - Hover only, not focus: rapid colour flashing on keyboard focus would be
 *   hostile — tab-focus keeps the existing scale/twinkle and brand colours.
 * - Skipped entirely under prefers-reduced-motion, same contract as the rest
 *   of the logo motion.
 * - Starts at a random pair each time so repeat visitors see a different run.
 */
( function () {
	if ( window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		return;
	}

	// [disc, ring] pairs — 14 of the 15 brand-book colorways (emerald-sunflower
	// dropped: its ring duplicated sunshine-navy's gold and its disc duplicated
	// mint-forest's green, so mid-cycle it read as a half-step), dark half on the
	// disc, bright half on the ring (assigned by relative luminance).
	const COLORWAYS = [
		[ '#0f766e', '#ff6f61' ], // teal-coral
		[ '#4a1d5e', '#ffb400' ], // plum-gold
		[ '#1e4d2b', '#ffb38a' ], // forest-peach
		[ '#8e1f2f', '#8fd6e8' ], // crimson-sky
		[ '#1743b3', '#ff8c42' ], // cobalt-tangerine
		[ '#26282b', '#a3e635' ], // charcoal-lime
		[ '#5c1a33', '#f4a6b8' ], // burgundy-blush
		[ '#101d42', '#6ee7b7' ], // midnight-mint
		[ '#a4441c', '#ffe1c4' ], // terracotta-cream
		[ '#6d28d9', '#ff5da2' ], // violet-hotpink
		[ '#0a2240', '#ffd23f' ], // sunshine-navy
		[ '#0b6e4f', '#cdeedd' ], // mint-forest
		[ '#37474f', '#fa8072' ], // slate-salmon
		[ '#111111', '#d4af37' ], // black-gold
	];
	const STEP_MS = 250; // 14 pairs ≈ one full loop every 3.5s

	document.querySelectorAll( '.site-logo' ).forEach( ( logo ) => {
		let timer = null;
		let i = Math.floor( Math.random() * COLORWAYS.length );

		const step = () => {
			const [ disc, ring ] = COLORWAYS[ i % COLORWAYS.length ];
			i += 1;
			logo.style.setProperty( '--mp-disc', disc );
			logo.style.setProperty( '--mp-ring', ring );
		};

		logo.addEventListener( 'pointerenter', () => {
			if ( timer !== null ) {
				return;
			}
			step();
			timer = window.setInterval( step, STEP_MS );
		} );

		logo.addEventListener( 'pointerleave', () => {
			if ( timer !== null ) {
				window.clearInterval( timer );
				timer = null;
			}
			// Back to brand: removing the properties lets the CSS defaults
			// (#0a2240 / #ff4713) win again, through the same transition.
			logo.style.removeProperty( '--mp-disc' );
			logo.style.removeProperty( '--mp-ring' );
		} );
	} );
} )();

/* 404 SKY PARALLAX
 * The three layers of the inline SVG in patterns/error-404.php drift at
 * different rates as the page scrolls, which is the whole of the depth cue —
 * there is no scale, no rotation and no fade.
 *
 * Driven off the band's own position, not window.scrollY: the offset is a
 * function of how far the band's centre sits from the viewport's centre, so it
 * reads the same whether the 404 is the whole page or someone deep-links past
 * it, and it is zero when the band is centred rather than snapping on load.
 *
 * Deliberate limits:
 * - Skipped entirely under prefers-reduced-motion, same contract as the rest
 *   of the theme's motion. The sky still renders; it just holds still.
 * - transform only (no top/left), read in the scroll handler and written in a
 *   rAF, so this never lays out and never reads layout inside the frame it
 *   writes.
 * - Bails when the band is off-screen, so a long page costs nothing while the
 *   404 is out of view.
 */
( function initSkyParallax() {
	const sky = document.querySelector( '.mp-404 .mp-sky' );
	if ( ! sky || window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		return;
	}

	const band = sky.closest( '.mp-404' );
	const layers = [ ...sky.querySelectorAll( '.mp-sky__layer' ) ].map( ( el ) => ( {
		el,
		depth: parseFloat( el.dataset.depth ) || 0,
	} ) );
	if ( ! band || ! layers.length ) {
		return;
	}

	// Peak travel for a depth of 1, in px. The layers run 0.12/0.3/0.55, so the
	// nearest moves at most 0.55 * 170 = ~94px either way — inside the 110px the
	// SVG overhangs the band on each side (design-system.css), so no layer can
	// ever drag an edge of the drawing into frame.
	const AMPLITUDE = 170;

	let ticking = false;

	const paint = () => {
		ticking = false;
		const rect = band.getBoundingClientRect();
		if ( rect.bottom < 0 || rect.top > window.innerHeight ) {
			return;
		}
		// -1 when the band sits a screen below centre, +1 a screen above.
		const bandCentre = rect.top + rect.height / 2;
		// Clamped so a long page cannot push a layer past the overhang.
		const raw = ( window.innerHeight / 2 - bandCentre ) / window.innerHeight;
		const offset = Math.max( -1, Math.min( 1, raw ) );
		layers.forEach( ( { el, depth } ) => {
			el.style.transform = 'translate3d(0,' + ( offset * depth * AMPLITUDE ).toFixed( 2 ) + 'px,0)';
		} );
	};

	const onScroll = () => {
		if ( ticking ) {
			return;
		}
		ticking = true;
		window.requestAnimationFrame( paint );
	};

	window.addEventListener( 'scroll', onScroll, { passive: true } );
	window.addEventListener( 'resize', onScroll, { passive: true } );
	paint();
} )();

export {};
